import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, interval, Observable, of, Subscriber } from 'rxjs';
import { catchError, filter, finalize, mergeMap, tap } from 'rxjs/operators';

import { API_URL_PREFIX } from '../../shared/services/basic.service';
import { ErrorHandlingService } from '../../shared/services/error-handling.service';
import { AlcmeonContextService } from '../alcmeon-context/alcmeon-context.service';
import { WebSocketNotificationTypeEnum } from '../websocket';
import { WebSocketAsyncRequestNotification, WebSocketNotification } from '../websocket/model';
import { WebSocketService } from '../websocket/websocket.service';
import { AsyncRequestResponse, AyncEndpointResponse } from './model';

@Injectable({
  providedIn: 'root',
})
export class AsyncRequestService {
  private pendingRequests: { [requestId: string]: AsyncRequest<unknown> } = {};

  private pendingWSAsyncRequestNotifs: { [requestId: string]: WebSocketAsyncRequestNotification } = {};

  constructor(
    private http: HttpClient,
    private alcmeonContext: AlcmeonContextService,
    wsService: WebSocketService,
    private ehs: ErrorHandlingService
  ) {
    // Every second, decrease the delay of all pending requests,
    // and poll the requests whose delay is 0 and are not currently being polled
    interval(1000)
      .pipe(
        mergeMap(() => from(Object.values(this.pendingRequests))),
        tap(request => request.decrementDelay()),
        filter(request => request.shouldBePolled()),
        mergeMap(request => this.pollRequest(request))
      )
      .subscribe();

    // subscribe to webssocket notifications
    wsService.message$
      .pipe(filter(isAsyncRequestWebSocketNotif))
      .subscribe(notif => this.handleOrQueueWebsocketNotif(notif));

    // The WS notifs that arrive via the subscription above
    // include async requests triggered by this class, but also those triggered by the angularJS side of the app
    // (that is, in src/ajs-app/services/async-request.services.js)
    // Hence, in order to avoid WS notifs accumulating and causing a memory leak,
    // we clear the pool periodically. In the unlikely event that we clear a notif that was triggered by this class,
    // the polling mechanism will fetch the response a couple of seconds later anyway.
    interval(10000).subscribe(() => (this.pendingWSAsyncRequestNotifs = {}));
  }

  post<T>(url, data): Observable<T> {
    return this.proxyObservable(this.http.post<AyncEndpointResponse<T>>(url, data), url);
  }

  put<T>(url, data): Observable<T> {
    return this.proxyObservable(this.http.put<AyncEndpointResponse<T>>(url, data), url);
  }

  get<T>(url, params?: HttpParams): Observable<T> {
    return this.proxyObservable(this.http.get<AyncEndpointResponse<T>>(url, { params }), url);
  }

  delete<T>(url): Observable<T> {
    return this.proxyObservable(this.http.delete<AyncEndpointResponse<T>>(url), url);
  }

  /**
   * Builds and returns an observable that mimics the observables returned by HttpClient,
   * but which will only emit/error when the underlying async request has completed/failed
   */
  private proxyObservable<T>(httpObservable: Observable<AyncEndpointResponse<T>>, url: string) {
    this.removeRequestsForURL(url);
    return new Observable<T>(subscriber => {
      this.callAsyncEndpoint(httpObservable, subscriber, url);
      return () => this.removeRequestsForSubscriber(subscriber); // return an unsubscription function
    });
  }

  /**
   * Subscribes to the given http observable to trigger the call to the async endpoint,
   * and hooks the callbacks for success and error
   */
  private callAsyncEndpoint<T>(
    httpObservable: Observable<AyncEndpointResponse<T>>,
    subscriber: Subscriber<T>,
    url: string
  ) {
    httpObservable.subscribe(
      startResponse => this.handleStartResponse(startResponse, subscriber, url),
      startError => this.handleStartError(startError, httpObservable, subscriber, url)
    );
  }

  /**
   * The async endpoint did reply with a success status:
   * let's register the pending async request
   * (or reply to the subscriber directly if the backend decided to reply synchronously)
   */
  private handleStartResponse<T>(startResponse: AyncEndpointResponse<T>, subscriber: Subscriber<T>, url: string) {
    if (!startResponse.is_really_async) {
      // the backend forced a synchronous call
      this.handleNotReallyAsyncStartResponse(startResponse, subscriber);
    } else {
      const requestId = startResponse.async_response.id;
      this.queuePendingRequest(requestId, subscriber, url);
    }
  }

  /**
   * If the backend decided to reply synchronously, let's forward the response to the subscriber right away
   */
  private handleNotReallyAsyncStartResponse<T>(startResponse: AyncEndpointResponse<T>, subscriber: Subscriber<T>) {
    const response = startResponse.sync_response;
    if (`${response.status}` === '200') {
      subscriber.next(response.data);
      subscriber.complete();
    } else {
      subscriber.error(
        new HttpErrorResponse({
          status: response.status,
          error: response.data,
        })
      );
    }
  }

  /**
   * The backend only allows a single pending request per user per handler,
   * and returns a 429 status code if we submit a new request before the previous one has completed.
   * In this case, we overwrite the pending async request with a new one
   * that allows us to wait for it to complete on the backend,
   * and we call again the async handler at that time.
   *
   * Note that subscriber to the original call will never be notified;
   * this is probably fine, as the user has retried the action anyway
   * and the new request to the async handler will update what the user expects.
   *
   * TODO check that this doesn't seem to cause a noticeable memory leak
   */
  private handleStartError<T>(
    startError: HttpErrorResponse,
    httpObservable: Observable<AyncEndpointResponse<T>>,
    subscriber: Subscriber<T>,
    url: string
  ) {
    if (startError.status === 429) {
      const previousPendingRequestId = startError.error.pending_request.id;
      new Observable(waitingSubscriber => {
        this.queuePendingRequest(previousPendingRequestId, waitingSubscriber, url);
      })
        .pipe(finalize(() => this.callAsyncEndpoint(httpObservable, subscriber, url)))
        .subscribe();
    } else {
      subscriber.error(new HttpErrorResponse({ status: -1 }));
    }
  }

  /**
   * Creates a new AsyncRequest and stores it in this._pendingRequests
   */
  private queuePendingRequest<T>(requestId: number, subscriber: Subscriber<T>, url: string) {
    const pendingRequest = new AsyncRequest(requestId, subscriber, url);
    this.pendingRequests[requestId] = pendingRequest;
    this.handleWSNotifIfAlreadyArrived(pendingRequest);
  }

  /**
   * If we have received a websocket notification about the completion of an async request
   * *before* the call to the async endpoint has completed,
   * let's handle the websocket notif right away.
   */
  private handleWSNotifIfAlreadyArrived<T>(pendingRequest: AsyncRequest<T>) {
    const requestId = pendingRequest.requestId;
    if (requestId in this.pendingWSAsyncRequestNotifs) {
      this.handleWebsocketNotif(pendingRequest, this.pendingWSAsyncRequestNotifs[requestId]);
      delete this.pendingWSAsyncRequestNotifs[requestId];
    }
  }

  /**
   * Returns an Observable which, when subscribed to,
   * will call /async-requests/<id> , to see if the backend has completed the async request.
   * If the request has completed, will emit its result to the original subscriber
   * If an error occured in the request, will also emit it to the subscriber
   * If the request hasn't completed yet, will set a new delay before checking again
   */
  private pollRequest<T>(pendingRequest: AsyncRequest<T>) {
    const requestId = pendingRequest.requestId;
    const companyId = this.alcmeonContext.companyId;
    pendingRequest.setIsRunning(true);
    const enpointURL = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/async-requests/${requestId}`;
    return this.http.get<AsyncRequestResponse<T>>(enpointURL, { observe: 'response' }).pipe(
      finalize(() => pendingRequest.setIsRunning(false)),
      tap(response => this.handleAsyncRequestResponse(pendingRequest, response)),
      catchError(() => {
        this.emitErrorAndRemoveRequest(pendingRequest, -1);
        // we don't return throwError(), to prevent the error from canceling the subscription set up in the constructor
        return of(null);
      })
    );
  }

  /**
   * Handles a sucessful response to a poll of a pending request.
   * The response can have status 202, which means we should retry again later,
   * or status 200, which means the async request has completed;
   * in which case we notify the original subscriber of the completion
   * (either sucessful or not, depending on the result of the async endpoint originally called)
   */
  private handleAsyncRequestResponse<T>(
    pendingRequest: AsyncRequest<T>,
    httpResponse: HttpResponse<AsyncRequestResponse<T>>
  ) {
    if (httpResponse.status === 202) {
      // status of the call to /async-requests/<requestId>
      pendingRequest.backoff();
    } else {
      const body = httpResponse.body;
      if (`${body.status}` === '200') {
        // status of the async request as executed by the backend handler (e.g. AsyncLinkedContactsGetHandler)
        this.emitSuccessAndRemoveRequest(pendingRequest, body.data);
      } else {
        this.emitErrorAndRemoveRequest(pendingRequest, body.status, body.data);
      }
    }
  }

  /**
   * Handles a websocket notification, received via the websocket service, that an async request has completed.
   * If we find a corresponding pending request, we forward the result to the original subscriber;
   * if we don't (either because the websocket notif arrived very quickly,
   * or because the request was sent by the angularJS side of the force),
   * we store it in the set of pending WS notifs.
   */
  private handleOrQueueWebsocketNotif(notif: WebSocketAsyncRequestNotification) {
    const requestId = notif.data.id;
    if (this.pendingRequests[requestId]) {
      const pendingRequest = this.pendingRequests[requestId];
      this.handleWebsocketNotif(pendingRequest, notif);
    } else {
      this.pendingWSAsyncRequestNotifs[requestId] = notif;
    }
  }

  /**
   * handles a websocket notif for which we have a corresponding pending request
   */
  private handleWebsocketNotif<T>(pendingRequest: AsyncRequest<T>, notif: WebSocketAsyncRequestNotification) {
    // eslint-disable-next-line no-prototype-builtins
    if (notif.data.hasOwnProperty('response')) {
      // responses shorter 4000 bytes are inlined in the websocket notif
      if (!pendingRequest.isRunning) {
        const response = notif.data.response;
        if (`${response.status}` === '200') {
          this.emitSuccessAndRemoveRequest(pendingRequest, response.data);
        } else {
          this.emitErrorAndRemoveRequest(pendingRequest, response.status, response.data);
        }
      }
    } else {
      // the response was > 4000 bytes, let's fetch it from the backend
      this.pollRequest(pendingRequest).subscribe();
    }
  }

  private removeRequestsForURL(url: string) {
    for (const request of this.allPendingRequests()) {
      if (request.url === url) {
        // forget previous requests to the same URL
        // (the user has probably moved on, and the UI that requested it might not even be displayed anymore)
        this.removeRequest(request);
      }
    }
  }

  private removeRequestsForSubscriber<T>(subscriber: Subscriber<T>) {
    for (const request of this.allPendingRequests()) {
      if (request.subscriber === subscriber) {
        this.removeRequest(request);
      }
    }
  }

  private allPendingRequests() {
    return Object.values(this.pendingRequests);
  }

  private removeRequest<T>(request: AsyncRequest<T>) {
    if (this.pendingRequests[request?.requestId]) {
      delete this.pendingRequests[request.requestId];
    }
  }

  private emitSuccessAndRemoveRequest<T>(request: AsyncRequest<T>, value: T) {
    if (this.pendingRequests[request.requestId]) {
      request.subscriber.next(value);
      request.subscriber.complete();
      this.removeRequest(request);
    }
  }

  private emitErrorAndRemoveRequest<T>(request: AsyncRequest<T>, status: number, error?) {
    if (this.pendingRequests[request.requestId]) {
      request.subscriber.error(new HttpErrorResponse({ status, error }));
      this.removeRequest(request);
    }
  }
}

/**
 * Represents a request to an async endpoint for which we don't have the reply yet
 */
class AsyncRequest<T> {
  isRunning = false;
  delayUntilRetry = 2;
  private currentBackoff = 2;

  constructor(public requestId: number, public subscriber: Subscriber<T>, public url: string) {}

  backoff() {
    this.currentBackoff *= 2;
    if (this.currentBackoff > 600) {
      this.currentBackoff = 600;
    }
    this.delayUntilRetry = Math.floor(Math.random() * this.currentBackoff);
    if (this.delayUntilRetry < 2) {
      this.delayUntilRetry = 2;
    }
  }

  decrementDelay() {
    this.delayUntilRetry--;
  }

  setIsRunning(isRunning: boolean) {
    this.isRunning = isRunning;
  }

  shouldBePolled() {
    return this.delayUntilRetry <= 0 && !this.isRunning;
  }
}

// helper functions outside of the service class so that we can use their type predicate signature
// in a .filter() to narrow the observable's type
function isAsyncRequestWebSocketNotif(notif: WebSocketNotification): notif is WebSocketAsyncRequestNotification {
  return notif.type === WebSocketNotificationTypeEnum.AsyncRequest;
}
